import { validateYupSchema } from "formik";
import { useEffect, useState } from "react";
import { shallow } from "zustand/shallow";
import { getIncidentFormValidator } from "../screens/User/Users/components/logIncident/validator";
import { useStoreWrapper } from "../store/store";
import {
  IFieldInstance,
  IncidentStatus,
  TabOptions,
} from "../types/fieldInstance.interface";
import { ValidationError } from "yup";
import { getViewModel } from "../utils/formGenUtils/viewModel";
import { defaultState } from "../components/Forms/incidentForm/methodsAndHandlers";
import { FormAction } from "../store/incident/reducer";

// Form ids already whole-form-revalidated this session. Module-scoped so it
// survives per-tab remounts but resets on reload; see the open effect below.
const wholeFormValidatedFormIds = new Set<string>();

const useFormValidation = (
  formik: any,
  allFields: { [x: string]: IFieldInstance[] },
  viewModel: any,
  rlViewModel: any,
  activeTab: Exclude<TabOptions, TabOptions.Approvals | TabOptions.Attachments>,
  formId: string,
  shouldValidateWholeForm: any
) => {
  const { forms, updateForm } = useStoreWrapper(
    (state) => ({
      forms: state.forms,
      updateForm: state.updateForm,
    }),
    shallow
  );
  const {
    formState,
    baseFormData: { incidentType },
  } = forms[formId] || defaultState;
  const [errors, setErrors] = useState<any>();
  const [canSubmitForm, setCanSubmitForm] = useState(false);
  const currentAllTabsState = formState?.tabState;
  const activeTabState = formState?.tabState?.[activeTab];
  // Already-submitted incidents relax fields flagged requiredForNewIncidentOnly (MARCC-772).
  const isSubmittedIncident =
    !!formState.incidentStatus &&
    formState.incidentStatus !== IncidentStatus.Draft;

  function _updateForm(formId: string, updateData: FormAction) {
    if (forms[formId]) {
      updateForm(formId, updateData);
    } else {
      return;
    }
  }
  // Merge only the given tabs into the latest tabState so parallel single-tab
  // writers (active-tab vs whole-form revalidation) don't clobber each other.
  function mergeTabState(payload: { [tab: string]: any }) {
    !formState.shouldResetForm &&
      _updateForm(formId, { type: "MERGE_TAB_STATE", payload });
  }

  async function validateForm(
    schema: any,
    isFormDirty: boolean,
    tab: Exclude<TabOptions, TabOptions.Approvals | TabOptions.Attachments>,
    staticValidateMode: boolean = false
  ) {
    // Return only this tab's patch (static mode) or merge it in (live mode) —
    // never write the whole tabState from a per-render snapshot.
    const actionOnValidation = (tabPatch: any) => {
      if (staticValidateMode) {
        return { ...currentAllTabsState?.[tab], ...tabPatch };
      } else {
        mergeTabState({ [tab]: tabPatch });
      }
    };
    try {
      const validation = await validateYupSchema(formik.values, schema);
      if (validation) {
        return actionOnValidation({ isFormDirty, isFormValid: true });
      }
    } catch (e) {
      if (e instanceof ValidationError) {
        const tabErrors = {} as any;
        e.inner.forEach((err, index) => {
          tabErrors[err.path || `${tab}-error-${index}`] = err.message;
        });
        setErrors({
          [tab]: tabErrors,
        });
        return actionOnValidation({ isFormDirty, isFormValid: false });
      }
    }
  }

  function getFormValidation() {
    // Use formik.dirty as the source of truth for the active tab.
    // Previously this used `activeTabState?.isFormDirty || formik.dirty` which
    // created a one-way latch: once dirty was true it could never clear, causing
    // the "Discard unsubmitted changes?" modal to appear even when no edits were made.
    const isFormDirty = formik.dirty;
    const fields = allFields[activeTab] as IFieldInstance[];
    const schema = getIncidentFormValidator(
      fields,
      viewModel,
      rlViewModel,
      isSubmittedIncident
    );
    validateForm(schema, isFormDirty, activeTab);
  }

  function getWholeFormValidation() {
    // Build the viewModel over the FULL field set (like the active-tab path),
    // not the default subset — otherwise branched/conditional fields get no
    // viewModel entry and their required-ness is silently skipped.
    const allFlatFields = Object.values(allFields).flat() as IFieldInstance[];
    const staticViewModel = getViewModel(
      formik.values,
      incidentType,
      activeTab,
      undefined,
      true,
      allFlatFields
    );
    const validatedTabs: { [tab: string]: any } = {};
    const promises = Object.keys(currentAllTabsState)
      .filter((tab) => tab !== TabOptions.Approvals)
      .map(async (tab) => {
        const fields = allFields[tab] as IFieldInstance[];
        const schema = getIncidentFormValidator(
          fields,
          staticViewModel,
          rlViewModel,
          isSubmittedIncident
        );
        try {
          const tabState = await validateForm(schema, false, tab as any, true);
          validatedTabs[tab] = { ...tabState, visited: true };
        } catch (e) {
          // leave this tab out of the merge on unexpected error
        }
      });
    // Merge only the tabs we actually revalidated, so a parallel active-tab
    // write isn't overwritten by a stale whole-tabState snapshot.
    Promise.all(promises)
      .then(() => {
        mergeTabState(validatedTabs);
      })
      .catch((error) => {
        console.error("Error in processing tabs:", error);
      });
  }

  useEffect(() => {
    // Submitted incidents can carry stale "valid" tab state from before a field
    // became required, so force one whole-form revalidation per open.
    const needsRevalidationOnOpen =
      !!formState.incidentStatus &&
      formState.incidentStatus !== IncidentStatus.Draft &&
      !wholeFormValidatedFormIds.has(formId);
    if (
      (shouldValidateWholeForm.current || needsRevalidationOnOpen) &&
      formik.values["Resident__c"] &&
      formState.incidentStatus
    ) {
      getWholeFormValidation();
      shouldValidateWholeForm.current = false;
      wholeFormValidatedFormIds.add(formId);
    }
  }, [formik.values]);

  useEffect(() => {
    getFormValidation();
    // viewModel/rlViewModel start stale on a tab remount and correct once
    // formik.values load, so re-validate when they change too.
  }, [formik.dirty, formik.errors, formik.values, viewModel, rlViewModel]);

  useEffect(() => {
    const isAnyFormDirty =
      currentAllTabsState &&
      Object.keys(currentAllTabsState).some(
        (key) =>
          currentAllTabsState[key as keyof typeof currentAllTabsState]
            ?.isFormDirty
      );
    if (isAnyFormDirty) {
      !formState.hasUnsavedChanges &&
        _updateForm(formId, {
          type: "UPDATE_FORM_STATE",
          payload: { hasUnsavedChanges: true },
        });
    } else {
      _updateForm(formId, {
        type: "UPDATE_FORM_STATE",
        payload: { hasUnsavedChanges: false },
      });
    }
  }, [formik.values, formState.tabState?.[activeTab].isFormDirty]);

  useEffect(() => {
    if (!activeTabState?.visited) {
      mergeTabState({ [activeTab]: { visited: true } });
    }
  }, []);

  useEffect(() => {
    const excludedTabs = [
      TabOptions.Approvals,
      TabOptions.Conclusion,
      TabOptions.Attachments,
    ];
    const _canSubmitForm =
      !!currentAllTabsState &&
      Object.entries(currentAllTabsState) // Get key-value pairs
        .filter(([key, _]) => !excludedTabs.includes(key as TabOptions)) // Exclude 'conclusions and approvals'
        .every(([, value]) => !!value.isFormValid);

    setCanSubmitForm(_canSubmitForm);
  }, [formState.tabState, formik.values]);

  return {
    errors,
    canSubmitForm,
  };
};

export default useFormValidation;
